<script>
import TalkizyAPI from '@app/services/API/Talkizy';
import meetingTitleEditMixin from '@app/mixins/meetingTitleEdit';
import isMeetingFinished, { MEETING_LEAD_MS } from '@shared/utils/meeting';

const STATUS_MAP = {
  scheduled: { label: 'Planifiée', type: 'is-success' },
  started: { label: 'En cours', type: 'is-success' },
  ended: { label: 'Terminée', type: 'is-light' },
};

export default {
  name: 'ClasseVirtuelleSingle',
  mixins: [meetingTitleEditMixin],
  head: {
    title: 'Classes virtuelles',
  },
  provide() {
    return {
      meetingStore: this.meetingStore,
    };
  },
  data() {
    return {
      meetingStore: {
        meeting: null,
        isLoading: false,
      },
      // Horloge réactive pour réévaluer l'état « Rejoindre » sans rechargement.
      now: Date.now(),
      // Timers d'activation du bouton « Rejoindre » (cf. setupJoinTimer) : armés
      // seulement à l'approche de l'ouverture, stoppés dès qu'il devient
      // rejoignable / terminé.
      joinTimer: null,
      joinStartTimeout: null,
    };
  },
  computed: {
    meetingId() {
      return this.$route.params.meetingId;
    },
    meeting() {
      return this.meetingStore.meeting;
    },
    canEdit() {
      return this.meeting && this.meeting.status === 'scheduled' && !isMeetingFinished(this.meeting);
    },
    status() {
      if (!this.meeting) return null;
      if (isMeetingFinished(this.meeting)) {
        return { label: 'Terminée', type: 'is-dark' };
      }
      return STATUS_MAP[this.meeting.status] || { label: this.meeting.status, type: 'is-light' };
    },
    // Mêmes critères que la carte de la liste (VisioCard) : le back ne fournit
    // join_url qu'au viewer autorisé (animateur principal ou co-animateur).
    canJoinAsHost() {
      return Boolean(this.meeting && this.meeting.join_url);
    },
    canJoin() {
      if (!this.meeting) return false;
      if (isMeetingFinished(this.meeting, this.now)) return false;
      if (this.meeting.status === 'started') return true;
      if (!this.meeting.starts_at) return false;
      return this.now >= new Date(this.meeting.starts_at).getTime() - MEETING_LEAD_MS;
    },
  },
  mounted() {
    this.fetchMeeting();
  },
  beforeDestroy() {
    this.clearJoinTimers();
  },
  methods: {
    async fetchMeeting() {
      this.meetingStore.isLoading = true;
      try {
        this.meetingStore.meeting = await TalkizyAPI.getMeeting(this.meetingId);
        this.setupJoinTimer();
      } catch (e) {
        this.$buefy.toast.open({ message: 'Impossible de charger la classe virtuelle.', type: 'is-danger' });
      } finally {
        this.meetingStore.isLoading = false;
      }
    },
    // Arme le timer d'activation du bouton « Rejoindre » UNIQUEMENT quand utile :
    //  - inutile si l'utilisateur n'est pas hôte (pas de join_url) ;
    //  - inutile si la classe est déjà rejoignable (bouton déjà actif) ;
    //  - inutile si la classe est terminée ;
    //  - pas trop tôt : l'intervalle (1 min) n'est armé qu'à ~1 min de
    //    l'ouverture (starts_at - MEETING_LEAD), via un setTimeout ;
    //  - pas trop tard : dès que le bouton devient rejoignable (ou la classe
    //    terminée), on stoppe l'intervalle.
    setupJoinTimer() {
      this.clearJoinTimers();

      if (!this.canJoinAsHost) return;
      if (isMeetingFinished(this.meeting, Date.now())) return;
      if (this.canJoin) return;
      if (!this.meeting.starts_at) return;

      const openAt = new Date(this.meeting.starts_at).getTime() - MEETING_LEAD_MS;
      const msUntilOpen = openAt - Date.now();

      const startInterval = () => {
        this.now = Date.now();
        if (this.canJoin) return; // déjà rejoignable : intervalle inutile
        this.joinTimer = setInterval(() => {
          this.now = Date.now();
          if (this.canJoin || isMeetingFinished(this.meeting, this.now)) {
            this.clearJoinTimers();
          }
        }, 60000);
      };

      if (msUntilOpen <= 60000) {
        // Ouverture imminente (dernière minute) : on commence à ticker de suite.
        startInterval();
      } else {
        // Sinon on patiente jusqu'à ~1 min avant l'ouverture avant de ticker.
        this.joinStartTimeout = setTimeout(startInterval, msUntilOpen - 60000);
      }
    },
    clearJoinTimers() {
      if (this.joinTimer) {
        clearInterval(this.joinTimer);
        this.joinTimer = null;
      }
      if (this.joinStartTimeout) {
        clearTimeout(this.joinStartTimeout);
        this.joinStartTimeout = null;
      }
    },
  },
};
</script>

<template>
  <div class="cvsingle">
    <p class="mb-4">
      <router-link :to="{ name: 'classe_virtuelle' }">
        <b-icon size="is-small" icon="chevron-left" />
        Retour aux classes virtuelles
      </router-link>
    </p>

    <div class="cvsingle_head">
      <h1 class="title is-flex is-align-items-center">
        <span v-if="meeting">{{ meeting.title }}</span>
        <span v-else>Classe virtuelle</span>
        <b-button
          v-if="canEdit"
          type="is-text"
          class="py-0 h-auto valign-middle ml-1"
          icon-left="pencil-alt"
          size="is-small"
          title="Modifier le titre"
          @click.stop.prevent="editMeetingTitle(meeting, (m) => { meetingStore.meeting = m; })"
        />
        <span v-if="status" class="tag ml-3">
          <b-icon class="mr-1 is-size-9" icon="circle" :type="status.type" />
          {{ status.label }}
        </span>
      </h1>

      <!-- Bouton « Rejoindre » : mêmes critères que la liste. À droite au niveau
           du titre ; passe sous le titre en responsive (flex-wrap + gap). -->
      <div v-if="canJoinAsHost" class="cvsingle_join">
        <a
          v-if="canJoin"
          :href="meeting.join_url"
          target="_blank"
          rel="noopener"
        >
          <b-button type="is-primary">
            Rejoindre
          </b-button>
        </a>
        <b-tooltip
          v-else
          label="Ce bouton sera disponible quelques minutes avant le début de la visio"
          position="is-left"
          multilined
        >
          <b-button type="is-primary" disabled>
            Rejoindre
          </b-button>
        </b-tooltip>
      </div>
    </div>

    <p v-if="meeting && meeting.main_animator" class="is-size-7 mb-5">
      <b-icon icon="chalkboard-teacher" size="is-small" class="mr-1" />
      Animée par <strong>{{ meeting.main_animator }}</strong>
    </p>

    <div class="tabs mb-6">
      <ul>
        <router-link
          tag="li"
          :to="{ name: 'classe_virtuelle_meeting_settings', params: $route.params }"
          exact-active-class="is-active"
        >
          <a>Paramètres</a>
        </router-link>
        <router-link
          tag="li"
          :to="{ name: 'classe_virtuelle_meeting_learners', params: $route.params }"
          exact-active-class="is-active"
        >
          <a>Apprenants</a>
        </router-link>
        <router-link
          tag="li"
          :to="{ name: 'classe_virtuelle_meeting', params: $route.params }"
          exact-active-class="is-active"
        >
          <a>Statistiques</a>
        </router-link>
      </ul>
    </div>

    <router-view />
  </div>
</template>

<style lang="scss" scoped>
// Titre à gauche, bouton « Rejoindre » à droite au niveau du titre. Le gap gère
// l'espacement horizontal ET vertical quand le bouton passe à la ligne (mobile),
// évitant tout chevauchement.
.cvsingle_head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-bottom: 1rem;

  .title {
    margin-bottom: 0;
    min-width: 0;
  }
}

.cvsingle_join {
  flex-shrink: 0;
}
</style>
